/**
 * =============================================================================
 * ReferralAffiliateSelector
 * Versão: 1.0.0 (2026-04-26)
 *
 * Descrição:
 *   Seletor de afiliado com busca server-side, no padrão visual de
 *   `SellerSelector` / `CrmUserFilter`. Permite localizar afiliados por
 *   nome, e-mail, telefone ou CPF/CNPJ — tudo via RPC paginada
 *   `get_referral_affiliates_paginated` (já protegida por RLS/admin).
 *
 *   Usa Popover + Command (cmdk) com debounce de 300ms para evitar floods.
 *
 * Props:
 *   - value: ID do afiliado selecionado (ou null).
 *   - onChange: callback ao selecionar/limpar.
 *   - placeholder: texto exibido quando nada está selecionado.
 * =============================================================================
 */
import * as React from 'react';
import { Check, ChevronDown, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Skeleton } from '@/components/ui/skeleton';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { useReferralAffiliatesPaginated } from '@/hooks/useReferralAffiliatesPaginated';
import { createLogger } from '@/lib/logger';

const logger = createLogger('ReferralAffiliateSelector');

interface ReferralAffiliateSelectorProps {
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  className?: string;
}

/** Hook utilitário simples de debounce. */
function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export function ReferralAffiliateSelector({
  value,
  onChange,
  placeholder = 'Filtrar por afiliado...',
  className,
}: ReferralAffiliateSelectorProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  // Busca paginada server-side (1 página de 20 resultados é suficiente para o seletor)
  const { affiliates, isFetching, error } = useReferralAffiliatesPaginated(
    {
      search: debouncedSearch || null,
      sort_by: 'full_name',
      sort_dir: 'asc',
    },
    { page: 1, pageSize: 20 },
  );

  if (error) {
    logger.warn('Erro ao buscar afiliados no seletor', error);
  }

  // Localiza o afiliado selecionado dentro do batch atual (para exibir o trigger).
  // Quando ainda não está no resultado, mostramos um placeholder discreto com o ID truncado.
  const selected = React.useMemo(
    () => affiliates.find((a) => a.id === value) || null,
    [affiliates, value],
  );

  const handleSelect = (id: string) => {
    onChange(id === value ? null : id);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            // Padrão visual igual aos demais filtros rápidos (LIST_FILTERS_*):
            // altura/tipografia compactas + texto preto (foreground) tanto
            // selecionado quanto vazio, para casar com Inputs/Selects da linha.
            'w-full h-6 sm:h-8 text-[11px] sm:text-sm justify-between font-normal text-left text-foreground',
            className,
          )}
        >
          {selected ? (
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <SmartAvatar
                src={selected.avatar_url}
                name={selected.full_name}
                email={selected.email}
                size="xs"
              />
              <div className="flex flex-col min-w-0 flex-1 leading-tight">
                <span className="truncate">
                  {selected.full_name || selected.email || 'Sem nome'}
                </span>
                {selected.email && selected.full_name && (
                  <span className="truncate text-[10px] text-muted-foreground hidden sm:inline">
                    {selected.email}
                  </span>
                )}
              </div>
            </div>
          ) : value ? (
            <span className="truncate">
              Afiliado #{value.substring(0, 8).toUpperCase()}
            </span>
          ) : (
            <span className="truncate">{placeholder}</span>
          )}

          <div className="flex items-center gap-1 shrink-0 ml-2">
            {value && (
              <span
                role="button"
                aria-label="Limpar"
                onClick={handleClear}
                className="hover:bg-muted rounded-sm p-0.5"
              >
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[360px] p-0"
        align="start"
        // Evita roubar foco do input de busca
        onOpenAutoFocus={(e) => {
          // permite cmdk gerenciar foco no input internamente
          e.preventDefault();
        }}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por nome, e-mail, telefone ou CPF/CNPJ..."
            value={search}
            onValueChange={setSearch}
            autoFocus
          />
          <CommandList>
            {isFetching ? (
              <div className="p-2 space-y-2">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-2 p-2">
                    <Skeleton className="h-7 w-7 rounded-full" />
                    <div className="flex-1 space-y-1">
                      <Skeleton className="h-3 w-2/3" />
                      <Skeleton className="h-2.5 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <CommandEmpty>Nenhum afiliado encontrado.</CommandEmpty>
                <CommandGroup>
                  {affiliates.map((a) => {
                    // Linha 2: e-mail (telefone/CPF não vêm no payload da RPC
                    // de listagem por questão de segurança — busca por eles
                    // funciona via search server-side, mas a exibição é
                    // mantida discreta com apenas o e-mail).
                    const meta = a.email || '';

                    return (
                      <CommandItem
                        key={a.id}
                        value={a.id}
                        onSelect={() => handleSelect(a.id)}
                        className="flex items-center gap-2 py-2"
                      >
                        <SmartAvatar
                          src={a.avatar_url}
                          name={a.full_name}
                          email={a.email}
                          size="xs"
                        />
                        <div className="flex flex-col min-w-0 flex-1 leading-tight">
                          <span className="truncate text-sm">
                            {a.full_name || a.email || 'Sem nome'}
                          </span>
                          {meta && (
                            <span className="truncate text-[11px] text-muted-foreground">
                              {meta}
                            </span>
                          )}
                        </div>
                        {value === a.id && (
                          <Check className="h-4 w-4 text-primary shrink-0" />
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
